import React, { useEffect, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";
import {
  StyledFormControl,
  StyledTextField,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import { H8TitleMedium } from "../../components/StyledComponents/Typography/Typography.tsx";
import { usePostCaseSaveList } from "../../api/queries/usePostCaseSaveList.ts";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { usePostCaseDeleteList } from "../../api/queries/usePostCaseDeleteSavedList.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "../../ThemeContext/ThemeContext.js";
// Create a broadcast channel for communicating saved list updates across tabs.
const channel =
  typeof window !== "undefined"
    ? new BroadcastChannel("case-saved-list-updates")
    : null;

const CaseManagementSavedListForm = ({
  type,
  placeholder,
  setFormOpen,
  refetchFilterNames,
  selectedList,
  setListItemButtonSelectedIndex,
  getValue,
  setFilterValue,
  setSelectedListName,
}) => {
  const { mutate: saveFilterList, isPending: isSaving } =
    usePostCaseSaveList();
  const {
    mutate: deleteFilterList,
    isPending: isDeleting,
  } = usePostCaseDeleteList();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { colors } = useTheme();
  const isInitialized = useRef(false);



  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      listName: "",
      listContent: "",
    },
  });

  const isEditing = useMemo(() => 
    selectedList && Object.keys(selectedList).length > 0, 
    [selectedList]
  );

  useEffect(() => {
    console.log("useEffect triggered:", { selectedList, isInitialized: isInitialized.current });
    if (selectedList && Object.keys(selectedList).length > 0) {
      const keys = Object.keys(selectedList)[0];
      const values = Object.values(selectedList)[0];
      const valuesString = Array.isArray(values)
        ? values.join("\n")
        : "";
      console.log("Resetting form with existing data:", { listName: keys, listContent: valuesString });
      reset({
        listName: keys,
        listContent: valuesString,
      });
      isInitialized.current = true;
    } else if (!selectedList && isInitialized.current) {
      console.log("Resetting form with empty data");
      reset({
        listName: "",
        listContent: "",
      });
      isInitialized.current = false;
    }
  }, [selectedList, reset]);

  const onSubmit = (data) => {
    if (!type) return;
    const finalListContent = data?.listContent
      ? data?.listContent
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      : [];
    const finalListName = data.listName.trim();
    saveFilterList(
      {
        filterType: type,
        filterListName: finalListName,
        filterList: finalListContent,
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            // Notify other tabs that the list of filters has changed.
            if (channel) {
              channel.postMessage({ type: "update" });
            }
            await refetchFilterNames();

            setToastMessage("List saved successfully.");
            setToastOpen(true);
            reset({
              listName: "",
              listContent: "",
            });
            setFormOpen(false);
            setListItemButtonSelectedIndex(null);
            setSelectedListName("");
          } else {
            setToastMessage(
              response.msg || "Error saving list"
            );
            setToastOpen(true);
          }
        },
        onError: async (error) => {
          setToastMessage(
            error.message || "Error saving list"
          );
          setToastOpen(true);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!type || !isEditing) return;
    const listName = getValues("listName");
    deleteFilterList(
      {
        filterType: type,
        filterListName: listName,
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            // Notify other tabs that the list of filters has changed.
            if (channel) {
              channel.postMessage({ type: "update" });
            }
            await refetchFilterNames();

            setListItemButtonSelectedIndex(null);
            reset({
              listName: "",
              listContent: "",
            });
            setSelectedListName("");
            setToastMessage("List deleted successfully.");
            setToastOpen(true);
            setFormOpen(false);
          } else {
            setToastMessage(
              response.msg || "Error deleting list."
            );
            setToastOpen(true);
          }
        },
        onError: (error) => {
          setToastMessage(
            error.message || "Error deleting list."
          );
          setToastOpen(true);
        },
      }
    );
  };

  const validateCaseNumbers = (value) => {
    const lines = value
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    for (let line of lines) {
      // Case numbers should be numeric (like 00001978, 00001979, etc.)
      if (!/^\d+$/.test(line)) {
        return "Invalid case number format. Use numeric format: 00001978, 00001979, etc.";
      }
    }
    return true;
  };

  const validateIssue = (value) => {
    const lines = value
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    for (let line of lines) {
      // Issue keywords should not be empty and should have reasonable length
      if (line.length === 0) {
        return "Issue keywords cannot be empty";
      }
      if (line.length > 100) {
        return "Issue keywords should be 100 characters or less";
      }
    }
    return true;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} key={selectedList ? JSON.stringify(selectedList) : 'new'}>
      <Box display="flex" flexDirection="column" gap={2}>
        {/* List Name */}
        <Box display="flex" flexDirection="column" gap={1}>
          <H8TitleMedium>Name</H8TitleMedium>
          <Controller
            name="listName"
            control={control}
            rules={{
              required: "Please enter a list name.",
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message:
                  "Letters, numbers, dash and underscore only.",
              },
              maxLength: {
                value: 32,
                message: "Up to 32 characters allowed.",
              },
            }}
            render={({ field }) => {
              console.log("List Name field render:", { 
                value: field.value, 
                onChange: field.onChange,
                onBlur: field.onBlur,
                name: field.name
              });
              return (
                <StyledTextField colors={colors}
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  label="List Name"
                  variant="outlined"
                  error={!!errors.listName}
                  helperText={errors.listName?.message}
                  fullWidth
                />
              );
            }}
          />
        </Box>

        {/* List Content */}
        <Box display="flex" flexDirection="column" gap={1}>
          <H8TitleMedium>Entries</H8TitleMedium>
          <Controller
            name="listContent"
            control={control}
            rules={{
              required: "Please enter at least one item.",
              validate: (value) => {
                const lines = value
                  .split("\n")
                  .map((l) => l.trim())
                  .filter(Boolean);
                if (lines.length === 0) {
                  return "At least one line is required.";
                }
                if (lines.length > 500) {
                  return "Maximum 500 lines allowed.";
                }

                // Case-specific validation
                if (type === "caseNo") {
                  return validateCaseNumbers(value);
                } else if (type === "issue") {
                  return validateIssue(value);
                }

                return true;
              },
            }}
            render={({ field }) => (
              <StyledFormControl colors={colors} fullWidth>
                <StyledTextField colors={colors}
                  {...field}
                  label="Items (one per line, max 500 lines)"
                  placeholder={placeholder}
                  variant="outlined"
                  multiline
                  minRows={10}
                  maxRows={10}
                  error={!!errors.listContent}
                  helperText={errors.listContent?.message}
                  fullWidth
                />
              </StyledFormControl>
            )}
          />
        </Box>

        {/* Submit */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={1}
          className="action-container"
        >
          {isEditing ? (
            <SecondaryButton colors={colors}
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              startIcon={<DeleteIcon />}
              endIcon={
                isDeleting ? (
                  <CircularProgress size={20} sx={{ color: colors.primary.main }} />
                ) : null
              }
            >
              {isDeleting ? "Deleting..." : "Delete list"}
            </SecondaryButton>
          ) : (
            <div />
          )}
          <PrimaryButton colors={colors}
            type="submit"
            disabled={!isValid || isSaving || isDeleting}
            endIcon={
              isSaving ? (
                <CircularProgress size={20} sx={{ color: colors.primary.main }} />
              ) : null
            }
          >
            {isSaving ? "Saving..." : "Save List"}
          </PrimaryButton>
        </Box>
      </Box>
    </form>
  );
};

export default CaseManagementSavedListForm;
